import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Target, Building2, CheckCircle, XCircle, Clock3, Plus, Edit, ChevronDown, ChevronUp, Info, Eye, Ear, Hand, Image, FileText, Calendar, Timer, Sunrise, Sun, Sunset } from 'lucide-react';
import { toast } from 'react-toastify';
import { Checkpoint, Asset } from '../../../types/checkpoint';
import { checkpointService } from '../../../services/checkpointService';
import Button from '../../../shared/components/ui/button/Button';
import { getDefaultBufferTime, getBufferTimeOptions, getMaxBufferTime, VALIDATION_LIMITS, validateCheckpointName, validateDescription, validateRequirement } from '../utils/checkpointUtils';

// Reusable toggle switch for enable/disable (matches the project-level checkpoint drawer UI)
const StatusToggle = ({ active, onToggle, disabled }: { active: boolean; onToggle: () => void; disabled?: boolean }) => (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      if (!disabled) onToggle();
    }}
    disabled={disabled}
    aria-label={active ? 'Disable checkpoint' : 'Enable checkpoint'}
    title={active ? 'Click to disable' : 'Click to enable'}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#339274] focus:ring-offset-2 ${active ? 'bg-[#339274]' : 'bg-gray-300'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ease-in-out ${active ? 'translate-x-6' : 'translate-x-1'
        }`}
    />
  </button>
);

interface AssetCheckpointsDrawerProps {
  assetData: {
    asset: Asset;
    checkpoints: Checkpoint[];
    totalCheckpoints: number;
    activeCheckpoints: number;
  } | null;
  open: boolean;
  onClose: () => void;
  onAdd?: (assetData: any) => void;
  onRefresh?: () => void;
}

// Frequency color mapping
const FREQUENCY_COLORS = {
  Daily: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
  Weekly: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', dot: 'bg-green-500' },
  Fortnightly: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  Monthly: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', dot: 'bg-purple-500' },
  Quarterly: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', dot: 'bg-orange-500' },
  'Half-Yearly': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
  Yearly: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', dot: 'bg-indigo-500' },
};

const formatRelativeTime = (dateString: string | Date) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return 'Yesterday';
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
  return `${Math.floor(diffInDays / 365)} years ago`;
};



export default function AssetCheckpointsDrawer({
  assetData,
  open,
  onClose,
  onAdd: _onAdd,
  onRefresh
}: AssetCheckpointsDrawerProps) {
  const [expandedCheckpoint, setExpandedCheckpoint] = useState<string | null>(null);
  const [editingCheckpoint, setEditingCheckpoint] = useState<Partial<Checkpoint> | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  const [newCheckpoint, setNewCheckpoint] = useState<{
    name: string;
    frequency: 'Daily' | 'Weekly' | 'Fortnightly' | 'Monthly' | 'Quarterly' | 'Half-Yearly' | 'Yearly';
    bufferTimeDays: number; // Grace period in days
    requirement: string; // Optional requirement field
    description: string;
    taskType: ('Visual' | 'Hearing' | 'Manual')[];
    isImageRequired: boolean;
    isInputRequired: boolean;
    executionPhase: string;
    executionTime: string;
  }>({
    name: '',
    frequency: 'Weekly',
    bufferTimeDays: getDefaultBufferTime('Weekly'),
    requirement: '',
    description: '',
    taskType: [],
    isImageRequired: false,
    isInputRequired: false,
    executionPhase: '',
    executionTime: ''
  });
  const [showSaveSuccess, setShowSaveSuccess] = useState<boolean>(false);
  const [showCreateSuccess, setShowCreateSuccess] = useState<boolean>(false);



  const getFrequencyColor = (freq: string) => {
    return FREQUENCY_COLORS[freq as keyof typeof FREQUENCY_COLORS] ||
      { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200', dot: 'bg-gray-500' };
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle size={16} className="text-green-600" />;
      case 'inactive':
        return <XCircle size={16} className="text-red-600" />;
      default:
        return <Clock3 size={16} className="text-gray-600" />;
    }
  };

  // Handle clicking on checkpoint to open/edit it
  const handleCheckpointClick = (checkpoint: Checkpoint) => {
    // Check if already expanded using either _id or id
    const checkpointId = checkpoint._id || checkpoint.id;
    if (expandedCheckpoint === checkpointId) {
      // If already expanded, close it
      setExpandedCheckpoint(null);
      setEditingCheckpoint(null);
    } else {
      // Close add form if open
      setShowAddForm(false);
      setNewCheckpoint({
        name: '',
        frequency: 'Weekly',
        bufferTimeDays: getDefaultBufferTime('Weekly'),
        requirement: '',
        description: '',
        taskType: [],
        isImageRequired: false,
        isInputRequired: false,
        executionPhase: '',
        executionTime: ''
      });

      // Open the checkpoint for editing - ensure _id is properly set
      if (!checkpointId) {
        console.error('Checkpoint has no ID:', checkpoint);
        return;
      }

      setEditingCheckpoint({
        _id: checkpointId,
        name: checkpoint.name,
        frequency: checkpoint.frequency,
        bufferTimeDays: checkpoint.bufferTimeDays || getDefaultBufferTime(checkpoint.frequency), // Include grace period
        requirement: checkpoint.requirement || '', // Include requirement field
        description: checkpoint.description || '',
        taskType: checkpoint.taskType || [],
        isImageRequired: checkpoint.isImageRequired || false,
        isInputRequired: (checkpoint.isInputRequired || 'None') as any,
        executionPhase: checkpoint.executionPhase || '',
        executionTime: checkpoint.executionTime || '',
        status: checkpoint.status || 'active'
      });
      setExpandedCheckpoint(checkpointId);
    }
  };

  // Handle edit button click (toggle expand/collapse)
  const handleEditButtonClick = (checkpoint: Checkpoint) => {
    handleCheckpointClick(checkpoint);
  };

  // Toggle enable/disable for a master checkpoint.
  // When disabled, the backend will skip this checkpoint when copying to new project assets
  // (existing project-level copies are not affected).
  const handleToggleStatus = async (checkpoint: Checkpoint) => {
    const checkpointId = checkpoint._id || checkpoint.id;
    if (!checkpointId || togglingId) return;

    try {
      setTogglingId(checkpointId);
      const response = await checkpointService.toggleCheckpointStatus(checkpointId);
      const newStatus = (response?.data as any)?.status
        || (checkpoint.status === 'active' ? 'inactive' : 'active');

      toast.success(
        `Checkpoint ${newStatus === 'active' ? 'enabled' : 'disabled'} successfully`
      );

      // Keep edit form in sync if the toggled checkpoint is being edited
      if (editingCheckpoint && editingCheckpoint._id === checkpointId) {
        setEditingCheckpoint(prev => prev ? { ...prev, status: newStatus } : prev);
      }

      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to toggle checkpoint status:', error);
      toast.error('Failed to update checkpoint status');
    } finally {
      setTogglingId(null);
    }
  };



  const handleSaveEdit = async () => {
    if (!editingCheckpoint || !editingCheckpoint._id) {
      console.error('No editing checkpoint or ID');
      return;
    }

    // Validate required fields using validation functions
    const nameValidation = validateCheckpointName(editingCheckpoint.name || '');
    if (!nameValidation.valid) {
      alert(nameValidation.error);
      return;
    }

    // Validate optional fields if provided
    if (editingCheckpoint.description) {
      const descValidation = validateDescription(editingCheckpoint.description);
      if (!descValidation.valid) {
        alert(descValidation.error);
        return;
      }
    }

    if (editingCheckpoint.requirement) {
      const reqValidation = validateRequirement(editingCheckpoint.requirement);
      if (!reqValidation.valid) {
        alert(reqValidation.error);
        return;
      }
    }

    if (!editingCheckpoint.frequency) {
      alert('Frequency is required');
      return;
    }

    try {
      setIsSaving(true);

      // Prepare the update data - include all editable fields
      const updateData: {
        name: string;
        frequency: 'Daily' | 'Weekly' | 'Fortnightly' | 'Monthly' | 'Quarterly' | 'Half-Yearly' | 'Yearly';
        bufferTimeDays?: number; // Include grace period
        requirement?: string;
        description?: string;
        taskType?: ('Visual' | 'Hearing' | 'Manual')[];
        isImageRequired?: boolean;
        isInputRequired?: boolean;
        executionPhase?: string;
        executionTime?: string;
      } = {
        name: editingCheckpoint.name,
        frequency: editingCheckpoint.frequency,
        bufferTimeDays: editingCheckpoint.bufferTimeDays, // Include grace period
        requirement: editingCheckpoint.requirement || undefined, // Include requirement field
        description: editingCheckpoint.description || undefined,
        taskType: (editingCheckpoint.taskType && editingCheckpoint.taskType.length > 0) ? editingCheckpoint.taskType : undefined,
        isImageRequired: editingCheckpoint.isImageRequired,
        isInputRequired: (editingCheckpoint.isInputRequired || 'None') as any,
        executionPhase: editingCheckpoint.executionPhase || undefined,
        executionTime: editingCheckpoint.executionTime || undefined
      };

      console.log('Sending update data:', updateData);

      // Make the PATCH call to update the checkpoint (using the correct HTTP method)
      console.log('Calling checkpointService.updateCheckpoint...');
      const response = await checkpointService.updateCheckpoint(
        editingCheckpoint._id,
        {
          ...updateData,
          isInputRequired: updateData.isInputRequired === true ? 'Both' : 'None'
        } as any
      );
      console.log('API response received:', response);

      // If status was changed in the form, call toggle-status so the master checkpoint
      // matches the new state (update endpoint does not accept status directly).
      const originalCheckpoint = assetData?.checkpoints.find(
        (c) => (c._id || c.id) === editingCheckpoint._id
      );
      const desiredStatus = (editingCheckpoint as any).status as 'active' | 'inactive' | undefined;
      if (desiredStatus && originalCheckpoint && originalCheckpoint.status !== desiredStatus) {
        try {
          await checkpointService.toggleCheckpointStatus(editingCheckpoint._id);
        } catch (toggleErr) {
          console.error('Failed to toggle checkpoint status during save:', toggleErr);
          toast.error('Checkpoint saved but failed to update status');
        }
      }

      // Extract the checkpoint data from the API response
      const updatedCheckpoint = response.data;

      // Update the editing state with the new data but keep it expanded
      setEditingCheckpoint({
        _id: updatedCheckpoint._id,
        name: updatedCheckpoint.name,
        frequency: updatedCheckpoint.frequency,
        bufferTimeDays: updatedCheckpoint.bufferTimeDays, // Include grace period
        requirement: updatedCheckpoint.requirement || '', // Include requirement field
        description: updatedCheckpoint.description || '',
        taskType: updatedCheckpoint.taskType || [],
        isImageRequired: updatedCheckpoint.isImageRequired || false,
        isInputRequired: (updatedCheckpoint.isInputRequired || 'None') as any,
        executionPhase: updatedCheckpoint.executionPhase || '',
        executionTime: updatedCheckpoint.executionTime || '',
        status: desiredStatus || updatedCheckpoint.status || 'active'
      });

      // Keep the checkpoint expanded to show the updated information
      // setExpandedCheckpoint remains the same (checkpoint._id)



      // Show success message and indicator
      setShowSaveSuccess(true);
      console.log('Checkpoint updated successfully:', updatedCheckpoint);

      // Call onRefresh callback to refresh the data in parent component
      if (onRefresh) {
        onRefresh();
      }

      // Hide success message after 3 seconds
      setTimeout(() => {
        setShowSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Failed to update checkpoint:', error);
      // You can add error handling here (show error message to user)
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingCheckpoint(null);
    setExpandedCheckpoint(null);
  };

  // Handle Add button click
  const handleAddButtonClick = () => {
    if (showAddForm) {
      // If form is open, close it and reset
      setShowAddForm(false);
      setNewCheckpoint({
        name: '',
        frequency: 'Weekly',
        bufferTimeDays: getDefaultBufferTime('Weekly'),
        requirement: '',
        description: '',
        taskType: [],
        isImageRequired: false,
        isInputRequired: false,
        executionPhase: '',
        executionTime: ''
      });
    } else {
      // Open the add form
      setShowAddForm(true);
      // Close any editing checkpoint
      setEditingCheckpoint(null);
      setExpandedCheckpoint(null);
    }
  };

  // Handle saving new checkpoint
  const handleSaveNewCheckpoint = async () => {
    if (!assetData) {
      console.error('Asset data is missing');
      return;
    }

    // Validate name field
    const nameValidation = validateCheckpointName(newCheckpoint.name);
    if (!nameValidation.valid) {
      alert(nameValidation.error);
      return;
    }

    // Validate description if provided
    if (newCheckpoint.description) {
      const descValidation = validateDescription(newCheckpoint.description);
      if (!descValidation.valid) {
        alert(descValidation.error);
        return;
      }
    }

    // Validate requirement if provided
    if (newCheckpoint.requirement) {
      const reqValidation = validateRequirement(newCheckpoint.requirement);
      if (!reqValidation.valid) {
        alert(reqValidation.error);
        return;
      }
    }

    if (!newCheckpoint.frequency) {
      alert('Frequency is required');
      return;
    }

    try {
      setIsSaving(true);

      // Prepare the create data
      const assetId = assetData.asset.id || assetData.asset._id;
      const projectTypeId = typeof assetData.asset.projectTypeId === 'object'
        ? assetData.asset.projectTypeId._id
        : assetData.asset.projectTypeId;

      if (!assetId) {
        console.error('Asset ID is missing:', assetData.asset);
        throw new Error('Asset ID is required but not found');
      }

      if (!projectTypeId) {
        console.error('Project Type ID is missing:', assetData.asset);
        throw new Error('Project Type ID is required but not found');
      }

      const createData: any = {
        name: newCheckpoint.name,
        frequency: newCheckpoint.frequency,
        bufferTimeDays: newCheckpoint.bufferTimeDays, // Include grace period
        requirement: newCheckpoint.requirement || undefined, // Include requirement field
        description: newCheckpoint.description || undefined,
        taskType: newCheckpoint.taskType.length > 0 ? newCheckpoint.taskType : undefined,
        isImageRequired: newCheckpoint.isImageRequired,
        isInputRequired: newCheckpoint.isInputRequired ? 'Both' : 'None',
        executionPhase: newCheckpoint.executionPhase || undefined,
        executionTime: newCheckpoint.executionTime || undefined,
        projectTypeId: projectTypeId,
        assetId: assetId,
        status: 'active' as const
      };

      console.log('Asset data structure:', assetData.asset);
      console.log('Extracted assetId:', assetId);
      console.log('Extracted projectTypeId:', projectTypeId);
      console.log('Creating new checkpoint:', createData);

      // Create the checkpoint
      const response = await checkpointService.createCheckpoint(createData);
      console.log('Checkpoint created successfully:', response);

      // Reset the form and close it
      setNewCheckpoint({
        name: '',
        frequency: 'Weekly',
        bufferTimeDays: getDefaultBufferTime('Weekly'),
        requirement: '',
        description: '',
        taskType: [],
        isImageRequired: false,
        isInputRequired: false,
        executionPhase: '',
        executionTime: ''
      });
      setShowAddForm(false);

      // Call the onRefresh callback to refresh the parent component data
      if (onRefresh) {
        onRefresh();
      }

      // Show success message and indicator
      setShowCreateSuccess(true);
      console.log('New checkpoint created successfully');

      // Hide success message after 3 seconds
      setTimeout(() => {
        setShowCreateSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Failed to create checkpoint:', error);
      // You can add error handling here (show error message to user)
    } finally {
      setIsSaving(false);
    }
  };

  // Handle canceling new checkpoint creation
  const handleCancelNewCheckpoint = () => {
    setNewCheckpoint({
      name: '',
      frequency: 'Weekly',
      bufferTimeDays: getDefaultBufferTime('Weekly'),
      requirement: '',
      description: '',
      taskType: [],
      isImageRequired: false,
      isInputRequired: false,
      executionPhase: '',
      executionTime: ''
    });
    setShowAddForm(false);
  };

  if (!open || !assetData) return null;

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-9999 bg-black/40 bg-opacity-30"
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", ease: "easeInOut", duration: 0.3 }}
            className="fixed inset-y-0 right-0 z-9999 w-full max-w-4xl overflow-y-auto bg-white shadow-xl"
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#339274]/20 rounded-xl flex items-center justify-center">
                    <Building2 size={24} className="text-[#339274]" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Checkpoints for {assetData.asset.name}</h2>
                    <p className="text-sm text-gray-600">
                      {assetData.totalCheckpoints} checkpoints ({assetData.activeCheckpoints} active)
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                  aria-label="Close drawer"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 p-6 overflow-y-auto">
                {/* Asset Information */}
                <div className="bg-white rounded-xl p-4 border border-gray-200 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Building2 size={20} className="text-[#339274]" />
                    Asset Information
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">Asset Name</label>
                      <p className="text-gray-900 mt-1">{assetData.asset.name}</p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-600">Project Type</label>
                      <p className="text-gray-900 mt-1">
                        {typeof assetData.asset.projectTypeId === 'object' && assetData.asset.projectTypeId
                          ? assetData.asset.projectTypeId.name
                          : 'N/A'}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-600">Status</label>
                      <div className="flex items-center gap-2 mt-1">
                        {getStatusIcon(assetData.asset.status)}
                        <span className={`capitalize px-2 py-1 rounded-full text-xs font-medium ${assetData.asset.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                          }`}>
                          {assetData.asset.status}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-600">Total Checkpoints</label>
                      <p className="text-gray-900 mt-1 font-semibold">{assetData.totalCheckpoints}</p>
                    </div>
                  </div>
                </div>

                {/* Checkpoints Section */}
                <div className="bg-white rounded-xl p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                      <Target size={18} className="text-[#339274]" />
                      Checkpoints ({assetData.checkpoints.length})
                    </h3>

                    <Button
                      onClick={handleAddButtonClick}
                      variant="default"
                      size="sm"
                      className="inline-flex items-center gap-1.5 text-xs px-3 h-8"
                    >
                      <Plus size={14} />
                      {showAddForm ? 'Cancel' : 'Add'}
                    </Button>
                  </div>

                  {/* Add Checkpoint Form */}
                  {showAddForm && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.25, ease: "easeInOut" }}
                      className="bg-gray-50/60 border border-gray-200 rounded-lg p-4 mb-4"
                    >
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 mb-4">
                          <Plus size={18} className="text-[#339274]" />
                          <h4 className="text-sm font-semibold text-gray-900">Add New Checkpoint</h4>
                        </div>

                        {/* Checkpoint Name */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Checkpoint Name *
                          </label>
                          <input
                            type="text"
                            value={newCheckpoint.name}
                            onChange={(e) => setNewCheckpoint(prev => ({
                              ...prev,
                              name: e.target.value
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#339274] focus:border-transparent"
                            placeholder="Enter checkpoint name"
                            disabled={isSaving}
                          />
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>Min: 2 chars, Max: 200 chars</span>
                            <span className={newCheckpoint.name.length > 200 ? 'text-red-600 font-semibold' : newCheckpoint.name.length > 180 ? 'text-orange-600' : ''}>
                              {newCheckpoint.name.length}/200
                            </span>
                          </div>
                        </div>

                        {/* Requirement Field */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Requirement <span className="text-gray-500 text-xs">(Optional)</span>
                          </label>
                          <textarea
                            value={newCheckpoint.requirement}
                            onChange={(e) => setNewCheckpoint(prev => ({
                              ...prev,
                              requirement: e.target.value
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#339274] focus:border-transparent resize-y"
                            placeholder="Enter checkpoint requirement..."
                            rows={2}
                            disabled={isSaving}
                          />
                          <div className="flex justify-end text-xs text-gray-500 mt-1">
                            <span className={newCheckpoint.requirement.length > 1000 ? 'text-red-600 font-semibold' : newCheckpoint.requirement.length > 900 ? 'text-orange-600' : ''}>
                              {newCheckpoint.requirement.length}/1000
                            </span>
                          </div>
                        </div>

                        {/* Description Field */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description <span className="text-gray-500 text-xs">(Optional)</span>
                          </label>
                          <textarea
                            value={newCheckpoint.description}
                            onChange={(e) => setNewCheckpoint(prev => ({
                              ...prev,
                              description: e.target.value
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#339274] focus:border-transparent resize-y"
                            placeholder="Enter description..."
                            rows={2}
                            disabled={isSaving}
                          />
                          <div className="flex justify-end text-xs text-gray-500 mt-1">
                            <span className={newCheckpoint.description.length > 2000 ? 'text-red-600 font-semibold' : newCheckpoint.description.length > 1800 ? 'text-orange-600' : ''}>
                              {newCheckpoint.description.length}/2000
                            </span>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          {/* Task Type */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Task Type
                            </label>
                            <div className="flex gap-2 flex-wrap">
                              {['Visual', 'Hearing', 'Manual'].map((type) => (
                                <label key={type} className="inline-flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={newCheckpoint.taskType.includes(type as any)}
                                    onChange={(e) => {
                                      const newTypes = e.target.checked
                                        ? [...newCheckpoint.taskType, type as any]
                                        : newCheckpoint.taskType.filter((t) => t !== type);
                                      setNewCheckpoint(prev => ({ ...prev, taskType: newTypes }));
                                    }}
                                    className="rounded border-gray-300 text-primary focus:ring-primary"
                                    disabled={isSaving}
                                  />
                                  <span className="ml-2 text-sm text-gray-700">{type}</span>
                                </label>
                              ))}
                            </div>
                          </div>

                          {/* Input Required */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Input Required
                            </label>
                            <label className="inline-flex items-center">
                              <input
                                type="checkbox"
                                checked={newCheckpoint.isInputRequired}
                                onChange={(e) => setNewCheckpoint(prev => ({ ...prev, isInputRequired: e.target.checked }))}
                                className="rounded border-gray-300 text-primary focus:ring-primary"
                                disabled={isSaving}
                              />
                              <span className="ml-2 text-sm text-gray-700">Input Required</span>
                            </label>
                          </div>
                        </div>

                        {/* Execution Shift Selector */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Execution Shift <span className="text-gray-500 text-xs">(Auto-fetches execution time)</span>
                          </label>
                          <div className="grid grid-cols-3 gap-2">
                            {[
                              { id: 'Morning', label: 'Pre-Gen (AM)', time: '05:00-09:00', icon: Sunrise, color: 'bg-orange-50 text-orange-600 border-orange-200' },
                              { id: 'Standard', label: 'Standard (Day)', time: '09:00-18:00', icon: Sun, color: 'bg-yellow-50 text-yellow-600 border-yellow-200' },
                              { id: 'Evening', label: 'Post-Gen (PM)', time: '18:00-22:00', icon: Sunset, color: 'bg-indigo-50 text-indigo-600 border-indigo-200' },
                            ].map((shift) => {
                              const isSelected = newCheckpoint.executionPhase === shift.id;
                              return (
                                <button
                                  key={shift.id}
                                  type="button"
                                  onClick={() => {
                                    setNewCheckpoint(prev => ({
                                      ...prev,
                                      executionPhase: shift.id,
                                      executionTime: shift.time,
                                    }));
                                  }}
                                  disabled={isSaving}
                                  className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm border-2 transition-all ${isSelected
                                    ? `${shift.color} border-current shadow-md`
                                    : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                    } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                  <shift.icon className="w-4 h-4" />
                                  <span className="font-medium">{shift.label}</span>
                                </button>
                              );
                            })}
                          </div>
                          {newCheckpoint.executionTime && (
                            <p className="mt-2 text-xs text-gray-500">
                              Execution Time: <span className="font-medium text-gray-700">{newCheckpoint.executionTime}</span>
                            </p>
                          )}
                        </div>

                        <div className="flex items-center">
                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              checked={newCheckpoint.isImageRequired}
                              onChange={(e) => setNewCheckpoint(prev => ({ ...prev, isImageRequired: e.target.checked }))}
                              className="rounded border-gray-300 text-primary focus:ring-primary"
                              disabled={isSaving}
                            />
                            <span className="ml-2 text-sm text-gray-700">Image Required</span>
                          </label>
                        </div>

                        {/* Frequency Selection */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Frequency *
                          </label>
                          <select
                            value={newCheckpoint.frequency}
                            onChange={(e) => {
                              const frequency = e.target.value as 'Daily' | 'Weekly' | 'Fortnightly' | 'Monthly' | 'Quarterly' | 'Half-Yearly' | 'Yearly';
                              const maxBuffer = getMaxBufferTime(frequency);
                              const currentBuffer = newCheckpoint.bufferTimeDays || 0;
                              // If current buffer exceeds max for new frequency, set to default
                              const newBuffer = currentBuffer > maxBuffer ? getDefaultBufferTime(frequency) : currentBuffer;
                              setNewCheckpoint(prev => ({
                                ...prev,
                                frequency,
                                bufferTimeDays: newBuffer
                              }));
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#339274] focus:border-transparent"
                            disabled={isSaving}
                          >
                            {Object.keys(FREQUENCY_COLORS).map((freq) => (
                              <option key={freq} value={freq}>
                                {freq}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Execution Phase Selection */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Execution Phase (Days)
                          </label>
                          <p className="text-xs text-gray-600 mb-2">
                            Number of days after due date before checkpoint becomes overdue
                          </p>
                          <select
                            value={newCheckpoint.bufferTimeDays}
                            onChange={(e) => setNewCheckpoint(prev => ({
                              ...prev,
                              bufferTimeDays: parseInt(e.target.value)
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#339274] focus:border-transparent"
                            disabled={isSaving}
                          >
                            {getBufferTimeOptions(newCheckpoint.frequency).map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <div className="flex items-center gap-1.5 text-xs text-gray-600 mt-1">
                            <Info className="w-3 h-3 flex-shrink-0" />
                            <span>
                              Recommended: {getDefaultBufferTime(newCheckpoint.frequency)} day(s) for {newCheckpoint.frequency} frequency
                            </span>
                          </div>
                        </div>

                        {/* Animated Success Message */}
                        <AnimatePresence>
                          {showCreateSuccess && (
                            <motion.div
                              initial={{ opacity: 0, y: 16, scale: 0.98 }}
                              animate={{ opacity: 1, y: 0, scale: 1 }}
                              exit={{ opacity: 0, y: -16, scale: 0.98 }}
                              transition={{ duration: 0.35, type: "spring", bounce: 0.3 }}
                              className="rounded-lg border border-green-200 bg-green-50 shadow-sm p-3 flex items-center justify-center gap-2 mb-2"
                              style={{
                                boxShadow: "0 2px 16px 0 rgba(51,146,116,0.08)"
                              }}
                            >
                              <motion.span
                                initial={{ scale: 0.7, rotate: -20 }}
                                animate={{ scale: 1.1, rotate: 0 }}
                                transition={{ type: "spring", stiffness: 300, damping: 12, delay: 0.1 }}
                              >
                                <CheckCircle size={18} className="text-[#339274] drop-shadow" />
                              </motion.span>
                              <span className="text-sm font-medium text-green-700 tracking-tight">
                                Checkpoint created successfully!
                              </span>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        {/* Asset Info Display */}
                        <div className="bg-white rounded-md p-3 border border-gray-200">
                          <div className="text-xs text-gray-600 mb-1">Will be created for Asset:</div>
                          <div className="text-sm font-medium text-gray-900">
                            {assetData.asset.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            Project Type: {typeof assetData.asset.projectTypeId === 'object'
                              ? assetData.asset.projectTypeId.name
                              : 'N/A'}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3 pt-2">
                          <Button
                            onClick={handleSaveNewCheckpoint}
                            variant="default"
                            size="sm"
                            className="flex-1"
                            disabled={!newCheckpoint.name || !newCheckpoint.frequency || isSaving}
                          >
                            {isSaving ? 'Creating...' : 'Create Checkpoint'}
                          </Button>
                          <Button
                            onClick={handleCancelNewCheckpoint}
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            disabled={isSaving}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {assetData.checkpoints.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Target size={36} className="mx-auto mb-3 text-gray-300" />
                      <p className="text-sm font-medium">No checkpoints found</p>
                      <p className="text-xs">This asset doesn't have any checkpoints yet.</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {Object.keys(FREQUENCY_COLORS).map((freq) => {
                        const freqCheckpoints = assetData.checkpoints.filter(
                          (c) => c.frequency === freq
                        );
                        if (freqCheckpoints.length === 0) return null;

                        const freqColors = getFrequencyColor(freq);

                        return (
                          <div key={freq}>
                            {/* Frequency Header */}
                            <div
                              className={`flex items-center gap-2 mb-3 text-sm font-medium ${freqColors.text}`}
                            >
                              <span
                                className={`w-2 h-2 rounded-full ${freqColors.dot}`}
                              />
                              {freq} ({freqCheckpoints.length})
                            </div>

                            {/* Checkpoints List */}
                            <div className="space-y-3">
                              {freqCheckpoints.map((checkpoint) => {
                                const checkpointId = checkpoint._id || checkpoint.id;
                                const isExpanded = expandedCheckpoint === checkpointId;
                                const isEditing = editingCheckpoint && editingCheckpoint._id === checkpointId;

                                const isInactive = checkpoint.status === 'inactive';

                                return (
                                  <div
                                    key={checkpointId}
                                    className={`bg-white rounded-lg border shadow-sm transition-all duration-200 cursor-pointer ${isExpanded
                                      ? "border-[#339274]/30 bg-[#f9fafb]"
                                      : "border-gray-200 hover:bg-gray-50"
                                      } ${isInactive ? 'bg-gray-50 opacity-80' : ''}`}
                                    onClick={() => handleCheckpointClick(checkpoint)}
                                  >
                                    {/* Header */}
                                    <div className="p-4 flex items-start justify-between">
                                      <div className="flex items-start gap-3 flex-1">
                                        <div
                                          className="mt-0.5 flex-shrink-0"
                                          onClick={(e) => e.stopPropagation()}
                                          title={isInactive
                                            ? 'Disabled - will NOT be copied to new project assets'
                                            : 'Enabled - will be copied to new project assets'}
                                        >
                                          <StatusToggle
                                            active={!isInactive}
                                            onToggle={() => handleToggleStatus(checkpoint)}
                                            disabled={togglingId === checkpointId}
                                          />
                                        </div>

                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                                            <h4 className={`text-sm font-semibold leading-tight ${isInactive ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                                              {checkpoint.name}
                                            </h4>
                                            <span
                                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${isInactive
                                                ? 'bg-gray-100 text-gray-600 border-gray-300'
                                                : 'bg-green-50 text-green-700 border-green-200'
                                                }`}
                                            >
                                              {isInactive ? 'Disabled' : 'Enabled'}
                                            </span>
                                          </div>

                                          {/* Description */}
                                          {checkpoint.description && (
                                            <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                                              {checkpoint.description}
                                            </p>
                                          )}

                                          {/* Requirement */}
                                          {checkpoint.requirement && (
                                            <div className="mb-2">
                                              <div className="flex items-start gap-1.5">
                                                <FileText size={12} className="text-gray-400 mt-0.5 flex-shrink-0" />
                                                <p className="text-xs text-gray-600">
                                                  <span className="font-medium text-gray-700">Requirement:</span> {checkpoint.requirement}
                                                </p>
                                              </div>
                                            </div>
                                          )}

                                          {/* Task Type Badges */}
                                          {checkpoint.taskType && checkpoint.taskType.length > 0 && (
                                            <div className="flex flex-wrap items-center gap-1.5 mb-2">
                                              {checkpoint.taskType.includes('Visual') && (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-200 text-xs font-medium">
                                                  <Eye size={12} />
                                                  Visual
                                                </span>
                                              )}
                                              {checkpoint.taskType.includes('Hearing') && (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 border border-purple-200 text-xs font-medium">
                                                  <Ear size={12} />
                                                  Hearing
                                                </span>
                                              )}
                                              {checkpoint.taskType.includes('Manual') && (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-orange-50 text-orange-700 border border-orange-200 text-xs font-medium">
                                                  <Hand size={12} />
                                                  Manual
                                                </span>
                                              )}
                                            </div>
                                          )}

                                          {/* Input & Image Required Badges */}
                                          <div className="flex flex-wrap items-center gap-2 mb-2">
                                            {/* Input Required */}
                                            {checkpoint.isInputRequired && checkpoint.isInputRequired !== 'None' && (
                                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs font-medium">
                                                <FileText size={12} />
                                                {checkpoint.isInputRequired === 'Both' ? 'Input Required' : `${checkpoint.isInputRequired} Input`}
                                              </span>
                                            )}

                                            {/* Image Required */}
                                            {checkpoint.isImageRequired && (
                                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-pink-50 text-pink-700 border border-pink-200 text-xs font-medium">
                                                <Image size={12} />
                                                Image Required
                                              </span>
                                            )}
                                          </div>

                                          {/* Execution Details */}
                                          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600 mb-2">
                                            {/* Execution Shift/Phase */}
                                            {checkpoint.executionPhase && (
                                              <div className="flex items-center gap-1">
                                                <Calendar size={12} className="text-gray-400" />
                                                <span className="text-gray-600">
                                                  <span className="font-medium text-gray-700">Shift:</span> {
                                                    checkpoint.executionPhase === 'Morning' ? 'Pre-Gen (AM)' :
                                                      checkpoint.executionPhase === 'Standard' ? 'Standard (Day)' :
                                                        checkpoint.executionPhase === 'Evening' ? 'Post-Gen (PM)' :
                                                          checkpoint.executionPhase
                                                  }
                                                </span>
                                              </div>
                                            )}

                                            {/* Execution Time */}
                                            {checkpoint.executionTime && (
                                              <div className="flex items-center gap-1">
                                                <Timer size={12} className="text-gray-400" />
                                                <span className="text-gray-600">
                                                  <span className="font-medium text-gray-700">Time:</span> {checkpoint.executionTime}
                                                </span>
                                              </div>
                                            )}

                                            {/* Buffer Time */}
                                            {checkpoint.bufferTimeDays !== undefined && (
                                              <div className="flex items-center gap-1">
                                                <Info size={12} className="text-gray-400" />
                                                <span className="text-gray-600">
                                                  <span className="font-medium text-gray-700">Buffer:</span> {checkpoint.bufferTimeDays === 0 ? 'No buffer' : checkpoint.bufferTimeDays === 1 ? '1 day' : `${checkpoint.bufferTimeDays} days`}
                                                </span>
                                              </div>
                                            )}
                                          </div>

                                          {/* Metadata */}
                                          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 pt-2 border-t border-gray-100">
                                            <span>
                                              Created by{" "}
                                              <span className="font-medium text-gray-700">
                                                {typeof checkpoint.createdBy === "object"
                                                  ? checkpoint.createdBy.name
                                                  : "N/A"}
                                              </span>
                                            </span>
                                            <span>•</span>
                                            <span>
                                              Updated by{" "}
                                              <span className="font-medium text-gray-700">
                                                {typeof checkpoint.updatedBy === "object"
                                                  ? checkpoint.updatedBy.name
                                                  : "N/A"}
                                              </span>
                                              <span className="ml-1 text-gray-400">
                                                ({formatRelativeTime(checkpoint.updatedAt)})
                                              </span>
                                            </span>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Action Buttons */}
                                      <div className="flex items-center gap-2">
                                        {/* Expand/Collapse Icon */}
                                        {isExpanded ? (
                                          <ChevronUp size={16} className="text-gray-400" />
                                        ) : (
                                          <ChevronDown size={16} className="text-gray-400" />
                                        )}

                                        {/* Edit Button */}
                                        <Button
                                          onClick={() => handleEditButtonClick(checkpoint)}
                                          variant="outline"
                                          size="sm"
                                          className="p-1.5 rounded-md text-gray-400 hover:text-[#339274] hover:bg-[#339274]/10"
                                        >
                                          <Edit size={14} />
                                        </Button>
                                      </div>
                                    </div>

                                    {/* Expanded Details View (Read-only) */}
                                    {isExpanded && !isEditing && (
                                      <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: "auto" }}
                                        exit={{ opacity: 0, height: 0 }}
                                        transition={{ duration: 0.25, ease: "easeInOut" }}
                                        className="border-t border-gray-100 bg-gray-50/40 p-4"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <div className="space-y-4">
                                          <div className="flex items-center gap-2 mb-3">
                                            <Info size={16} className="text-[#339274]" />
                                            <h4 className="text-sm font-semibold text-gray-900">Checkpoint Details</h4>
                                          </div>

                                          {/* Details Grid */}
                                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {/* Description */}
                                            {checkpoint.description && (
                                              <div className="md:col-span-2">
                                                <label className="text-xs font-medium text-gray-600 mb-1 block">Description</label>
                                                <p className="text-sm text-gray-900 bg-white p-2 rounded border border-gray-200">
                                                  {checkpoint.description}
                                                </p>
                                              </div>
                                            )}

                                            {/* Requirement */}
                                            {checkpoint.requirement && (
                                              <div className="md:col-span-2">
                                                <label className="text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                                                  <FileText size={12} />
                                                  Requirement
                                                </label>
                                                <p className="text-sm text-gray-900 bg-white p-2 rounded border border-gray-200">
                                                  {checkpoint.requirement}
                                                </p>
                                              </div>
                                            )}

                                            {/* Task Type */}
                                            <div>
                                              <label className="text-xs font-medium text-gray-600 mb-2 block">Task Type</label>
                                              <div className="flex flex-wrap gap-1.5">
                                                {checkpoint.taskType && checkpoint.taskType.length > 0 ? (
                                                  <>
                                                    {checkpoint.taskType.includes('Visual') && (
                                                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-blue-50 text-blue-700 border border-blue-200 text-xs font-medium">
                                                        <Eye size={14} />
                                                        Visual
                                                      </span>
                                                    )}
                                                    {checkpoint.taskType.includes('Hearing') && (
                                                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-purple-50 text-purple-700 border border-purple-200 text-xs font-medium">
                                                        <Ear size={14} />
                                                        Hearing
                                                      </span>
                                                    )}
                                                    {checkpoint.taskType.includes('Manual') && (
                                                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-orange-50 text-orange-700 border border-orange-200 text-xs font-medium">
                                                        <Hand size={14} />
                                                        Manual
                                                      </span>
                                                    )}
                                                  </>
                                                ) : (
                                                  <span className="text-xs text-gray-500">Not specified</span>
                                                )}
                                              </div>
                                            </div>

                                            {/* Input Required */}
                                            <div>
                                              <label className="text-xs font-medium text-gray-600 mb-2 block">Input Required</label>
                                              <div className="flex items-center gap-2">
                                                {checkpoint.isInputRequired && checkpoint.isInputRequired !== 'None' ? (
                                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs font-medium">
                                                    <FileText size={14} />
                                                    Required
                                                  </span>
                                                ) : (
                                                  <span className="text-xs text-gray-500">Not required</span>
                                                )}
                                              </div>
                                            </div>

                                            {/* Image Required */}
                                            <div>
                                              <label className="text-xs font-medium text-gray-600 mb-2 block">Image Required</label>
                                              <div className="flex items-center gap-2">
                                                {checkpoint.isImageRequired ? (
                                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-pink-50 text-pink-700 border border-pink-200 text-xs font-medium">
                                                    <Image size={14} />
                                                    Required
                                                  </span>
                                                ) : (
                                                  <span className="text-xs text-gray-500">Not required</span>
                                                )}
                                              </div>
                                            </div>

                                            {/* Execution Phase */}
                                            <div>
                                              <label className="text-xs font-medium text-gray-600 mb-2 block flex items-center gap-1">
                                                <Calendar size={12} />
                                                Execution Phase
                                              </label>
                                              <p className="text-sm text-gray-900 bg-white p-2 rounded border border-gray-200">
                                                {checkpoint.executionPhase || 'Not specified'}
                                              </p>
                                            </div>

                                            {/* Execution Time */}
                                            <div>
                                              <label className="text-xs font-medium text-gray-600 mb-2 block flex items-center gap-1">
                                                <Timer size={12} />
                                                Execution Time
                                              </label>
                                              <p className="text-sm text-gray-900 bg-white p-2 rounded border border-gray-200">
                                                {checkpoint.executionTime || 'Not specified'}
                                              </p>
                                            </div>

                                            {/* Buffer Time */}
                                            <div>
                                              <label className="text-xs font-medium text-gray-600 mb-2 block flex items-center gap-1">
                                                <Info size={12} />
                                                Buffer Time (Days)
                                              </label>
                                              <p className="text-sm text-gray-900 bg-white p-2 rounded border border-gray-200">
                                                {checkpoint.bufferTimeDays !== undefined
                                                  ? checkpoint.bufferTimeDays === 0
                                                    ? 'No buffer'
                                                    : checkpoint.bufferTimeDays === 1
                                                      ? '1 day'
                                                      : `${checkpoint.bufferTimeDays} days`
                                                  : 'Not specified'}
                                              </p>
                                            </div>

                                            {/* Frequency */}
                                            <div>
                                              <label className="text-xs font-medium text-gray-600 mb-2 block">Frequency</label>
                                              <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${getFrequencyColor(checkpoint.frequency).bg
                                                } ${getFrequencyColor(checkpoint.frequency).text} ${getFrequencyColor(checkpoint.frequency).border} border`}>
                                                {checkpoint.frequency}
                                              </span>
                                            </div>
                                          </div>

                                          {/* Action Button */}
                                          <div className="pt-2 border-t border-gray-200" onClick={(e) => e.stopPropagation()}>
                                            <Button
                                              onClick={() => handleEditButtonClick(checkpoint)}
                                              variant="default"
                                              size="sm"
                                              className="w-full"
                                            >
                                              <Edit size={14} className="mr-1.5" />
                                              Edit Checkpoint
                                            </Button>
                                          </div>
                                        </div>
                                      </motion.div>
                                    )}

                                    {/* Expanded Edit Form */}
                                    {isExpanded && isEditing && (
                                      <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: "auto" }}
                                        exit={{ opacity: 0, height: 0 }}
                                        transition={{ duration: 0.25, ease: "easeInOut" }}
                                        className="border-t border-gray-100 bg-gray-50/60 p-4 rounded-b-lg"
                                        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside form
                                      >
                                        <div className="space-y-4">
                                          <div className="flex items-center gap-2 mb-4">
                                            <Edit size={18} className="text-[#339274]" />
                                            <h4 className="text-sm font-semibold text-gray-900">Edit Checkpoint</h4>
                                          </div>

                                          {/* Checkpoint Name */}
                                          <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                              Checkpoint Name *
                                            </label>
                                            <input
                                              type="text"
                                              value={editingCheckpoint.name || ''}
                                              onChange={(e) => setEditingCheckpoint(prev => ({
                                                ...prev,
                                                name: e.target.value
                                              }))}
                                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#339274] focus:border-transparent"
                                              placeholder="Enter checkpoint name"
                                              disabled={isSaving}
                                            />
                                          </div>

                                          {/* Requirement Field */}
                                          <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                              Requirement <span className="text-gray-500 text-xs">(Optional)</span>
                                            </label>
                                            <textarea
                                              value={editingCheckpoint.requirement || ''}
                                              onChange={(e) => setEditingCheckpoint(prev => ({
                                                ...prev,
                                                requirement: e.target.value
                                              }))}
                                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#339274] focus:border-transparent resize-y"
                                              placeholder="Enter checkpoint requirement..."
                                              rows={2}
                                              disabled={isSaving}
                                            />
                                          </div>

                                          {/* Description Field */}
                                          <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                              Description <span className="text-gray-500 text-xs">(Optional)</span>
                                            </label>
                                            <textarea
                                              value={editingCheckpoint.description || ''}
                                              onChange={(e) => setEditingCheckpoint(prev => ({
                                                ...prev,
                                                description: e.target.value
                                              }))}
                                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#339274] focus:border-transparent resize-y"
                                              placeholder="Enter description..."
                                              rows={2}
                                              disabled={isSaving}
                                            />
                                          </div>

                                          <div className="grid grid-cols-2 gap-4">
                                            {/* Task Type */}
                                            <div>
                                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Task Type
                                              </label>
                                              <div className="flex gap-2 flex-wrap">
                                                {['Visual', 'Hearing', 'Manual'].map((type) => (
                                                  <label key={type} className="inline-flex items-center">
                                                    <input
                                                      type="checkbox"
                                                      checked={(editingCheckpoint.taskType || []).includes(type as any)}
                                                      onChange={(e) => {
                                                        const currentTypes = editingCheckpoint.taskType || [];
                                                        const newTypes = e.target.checked
                                                          ? [...currentTypes, type as any]
                                                          : currentTypes.filter((t) => t !== type);
                                                        setEditingCheckpoint(prev => ({ ...prev, taskType: newTypes }));
                                                      }}
                                                      className="rounded border-gray-300 text-primary focus:ring-primary"
                                                      disabled={isSaving}
                                                    />
                                                    <span className="ml-2 text-sm text-gray-700">{type}</span>
                                                  </label>
                                                ))}
                                              </div>
                                            </div>

                                            {/* Input Required */}
                                            <div>
                                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Input Required
                                              </label>
                                              <label className="inline-flex items-center">
                                                <input
                                                  type="checkbox"
                                                  checked={editingCheckpoint.isInputRequired !== 'None' && !!editingCheckpoint.isInputRequired}
                                                  onChange={(e) => setEditingCheckpoint(prev => ({ ...prev, isInputRequired: (e.target.checked ? 'Both' : 'None') as any }))}
                                                  className="rounded border-gray-300 text-primary focus:ring-primary"
                                                  disabled={isSaving}
                                                />
                                                <span className="ml-2 text-sm text-gray-700">Input Required</span>
                                              </label>
                                            </div>
                                          </div>

                                          {/* Execution Shift Selector */}
                                          <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                              Execution Shift <span className="text-gray-500 text-xs">(Auto-fetches execution time)</span>
                                            </label>
                                            <div className="grid grid-cols-3 gap-2">
                                              {[
                                                { id: 'Morning', label: 'Pre-Gen (AM)', time: '05:00-09:00', icon: Sunrise, color: 'bg-orange-50 text-orange-600 border-orange-200' },
                                                { id: 'Standard', label: 'Standard (Day)', time: '09:00-18:00', icon: Sun, color: 'bg-yellow-50 text-yellow-600 border-yellow-200' },
                                                { id: 'Evening', label: 'Post-Gen (PM)', time: '18:00-22:00', icon: Sunset, color: 'bg-indigo-50 text-indigo-600 border-indigo-200' },
                                              ].map((shift) => {
                                                const isSelected = editingCheckpoint.executionPhase === shift.id;
                                                return (
                                                  <button
                                                    key={shift.id}
                                                    type="button"
                                                    onClick={() => {
                                                      setEditingCheckpoint(prev => ({
                                                        ...prev,
                                                        executionPhase: shift.id,
                                                        executionTime: shift.time,
                                                      }));
                                                    }}
                                                    disabled={isSaving}
                                                    className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm border-2 transition-all ${isSelected
                                                      ? `${shift.color} border-current shadow-md`
                                                      : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                                      } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                  >
                                                    <shift.icon className="w-4 h-4" />
                                                    <span className="font-medium">{shift.label}</span>
                                                  </button>
                                                );
                                              })}
                                            </div>
                                            {editingCheckpoint.executionTime && (
                                              <p className="mt-2 text-xs text-gray-500">
                                                Execution Time: <span className="font-medium text-gray-700">{editingCheckpoint.executionTime}</span>
                                              </p>
                                            )}
                                          </div>

                                          <div className="flex items-center flex-wrap gap-4">
                                            <label className="inline-flex items-center">
                                              <input
                                                type="checkbox"
                                                checked={editingCheckpoint.isImageRequired || false}
                                                onChange={(e) => setEditingCheckpoint(prev => ({ ...prev, isImageRequired: e.target.checked }))}
                                                className="rounded border-gray-300 text-primary focus:ring-primary"
                                                disabled={isSaving}
                                              />
                                              <span className="ml-2 text-sm text-gray-700">Image Required</span>
                                            </label>

                                            {/* Enable / Disable toggle */}
                                            <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-200">
                                              <span className="text-sm font-medium text-gray-700">Status</span>
                                              <StatusToggle
                                                active={(editingCheckpoint as any).status !== 'inactive'}
                                                onToggle={() => setEditingCheckpoint(prev => prev ? ({
                                                  ...prev,
                                                  status: ((prev as any).status === 'inactive' ? 'active' : 'inactive') as any
                                                }) : prev)}
                                                disabled={isSaving}
                                              />
                                              <span className={`text-xs font-semibold uppercase tracking-wide ${(editingCheckpoint as any).status === 'inactive' ? 'text-gray-500' : 'text-green-600'}`}>
                                                {(editingCheckpoint as any).status === 'inactive' ? 'Disabled' : 'Enabled'}
                                              </span>
                                            </div>
                                          </div>
                                          <p className="text-xs text-gray-500 -mt-1 flex items-start gap-1">
                                            <Info size={12} className="mt-0.5 flex-shrink-0" />
                                            <span>
                                              Disabled checkpoints are kept for existing projects but will <strong>not</strong> be copied when creating new project assets.
                                            </span>
                                          </p>

                                          {/* Frequency Selection */}
                                          <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                              Frequency *
                                            </label>
                                            <select
                                              value={editingCheckpoint.frequency || ''}
                                              onChange={(e) => {
                                                const frequency = e.target.value as 'Daily' | 'Weekly' | 'Fortnightly' | 'Monthly' | 'Quarterly' | 'Half-Yearly' | 'Yearly';
                                                const maxBuffer = getMaxBufferTime(frequency);
                                                const currentBuffer = editingCheckpoint.bufferTimeDays || 0;
                                                // If current buffer exceeds max for new frequency, set to default
                                                const newBuffer = currentBuffer > maxBuffer ? getDefaultBufferTime(frequency) : currentBuffer;
                                                setEditingCheckpoint(prev => ({
                                                  ...prev,
                                                  frequency,
                                                  bufferTimeDays: newBuffer
                                                }));
                                              }}
                                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#339274] focus:border-transparent"
                                              disabled={isSaving}
                                            >
                                              <option value="">Select Frequency</option>
                                              {Object.keys(FREQUENCY_COLORS).map((freq) => (
                                                <option key={freq} value={freq}>
                                                  {freq}
                                                </option>
                                              ))}
                                            </select>
                                          </div>

                                          {/* Execution Phase Selection */}
                                          <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                              Execution Phase (Days)
                                            </label>
                                            <p className="text-xs text-gray-600 mb-2">
                                              Number of days after due date before checkpoint becomes overdue
                                            </p>
                                            <select
                                              value={editingCheckpoint.bufferTimeDays || 0}
                                              onChange={(e) => setEditingCheckpoint(prev => ({
                                                ...prev,
                                                bufferTimeDays: parseInt(e.target.value)
                                              }))}
                                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#339274] focus:border-transparent"
                                              disabled={isSaving}
                                            >
                                              {getBufferTimeOptions(editingCheckpoint.frequency || 'Weekly').map((option) => (
                                                <option key={option.value} value={option.value}>
                                                  {option.label}
                                                </option>
                                              ))}
                                            </select>
                                            <div className="flex items-center gap-1.5 text-xs text-gray-600 mt-1">
                                              <Info className="w-3 h-3 flex-shrink-0" />
                                              <span>
                                                Recommended: {getDefaultBufferTime(editingCheckpoint.frequency || 'Weekly')} day(s) for {editingCheckpoint.frequency || 'Weekly'} frequency
                                              </span>
                                            </div>
                                          </div>

                                          {/* Success Message */}
                                          {showSaveSuccess && (
                                            <div className="bg-green-50 border border-green-200 rounded-md p-3 text-center">
                                              <div className="flex items-center justify-center gap-2 text-green-700">
                                                <CheckCircle size={16} className="text-green-600" />
                                                <span className="text-sm font-medium">Checkpoint updated successfully!</span>
                                              </div>
                                            </div>
                                          )}

                                          {/* Action Buttons */}
                                          <div className="flex gap-3 pt-2">
                                            <Button
                                              onClick={handleSaveEdit}
                                              variant="default"
                                              size="sm"
                                              className="flex-1"
                                              disabled={!editingCheckpoint.name || !editingCheckpoint.frequency || isSaving}
                                            >
                                              {isSaving ? 'Saving...' : 'Save Changes'}
                                            </Button>
                                            <Button
                                              onClick={handleCancelEdit}
                                              variant="outline"
                                              size="sm"
                                              className="flex-1"
                                              disabled={isSaving}
                                            >
                                              Cancel
                                            </Button>
                                          </div>
                                        </div>
                                      </motion.div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-6 border-t border-gray-200">
                  <Button
                    onClick={onClose}
                    variant="outline"
                    size="sm"
                    className="flex-1"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
